/**
 * Life4Earth V2 — Centralized Number Formatting
 *
 * Uses `Intl.NumberFormat` to render numbers in the user's locale.
 * When the language is Arabic, digits automatically render as
 * Arabic-Indic (٠-٩).  When English, Western digits are used.
 *
 * All functions read `l4e_language` from localStorage so they stay
 * in sync with the language switcher — same pattern as dateFormat.ts.
 *
 * NOTE: This replaces most uses of the older `localizeNumber()` in
 * rtl.ts.  The old function still works for edge cases where you
 * have a pre-formatted string (e.g. "SDG 14") and just need digit
 * substitution.
 */

/* ── Locale detection (shared with dateFormat.ts) ──────────────── */

function getLang(): string {
  try {
    return localStorage.getItem('l4e_language') || document.documentElement.lang || 'en';
  } catch {
    return 'en';
  }
}

/* ── Formatter cache ───────────────────────────────────────────── */

// Intl.NumberFormat is expensive to construct; cache by key.
const cache = new Map<string, Intl.NumberFormat>();

function getFormatter(opts: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const lang = getLang();
  const key = `${lang}|${JSON.stringify(opts)}`;
  let fmt = cache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(lang, opts);
    cache.set(key, fmt);
  }
  return fmt;
}

/* ── Public API ────────────────────────────────────────────────── */

/**
 * Format a plain number using the current locale.
 *
 * Examples (English): formatNumber(1234)       → "1,234"
 *                     formatNumber(3.14159, 2) → "3.14"
 * Examples (Arabic):  formatNumber(1234)       → "١٬٢٣٤"
 *                     formatNumber(3.14159, 2) → "٣٫١٤"
 */
export function formatNumber(
  value: number,
  maximumFractionDigits?: number,
): string {
  const opts: Intl.NumberFormatOptions = {};
  if (maximumFractionDigits !== undefined) {
    opts.maximumFractionDigits = maximumFractionDigits;
  }
  return getFormatter(opts).format(value);
}

/**
 * Format a number with an exact number of decimal places.
 * Useful for KPI values like SPI/CPI that always show 2 decimals.
 *
 * Examples: formatFixed(1.5, 2)  → "1.50" (en) / "١٫٥٠" (ar)
 *           formatFixed(42, 0)   → "42"   (en) / "٤٢"   (ar)
 */
export function formatFixed(
  value: number,
  fractionDigits: number,
): string {
  return getFormatter({
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * Format a percentage value.
 * Input is the actual percentage (e.g. 85.3 for 85.3%), NOT a ratio.
 * Appends the locale-appropriate "%" symbol.
 *
 * Examples: formatPercent(85.3, 1) → "85.3%" (en) / "٨٥٫٣٪"  (ar)
 *           formatPercent(100, 0)  → "100%"  (en) / "١٠٠٪"   (ar)
 */
export function formatPercent(
  value: number,
  fractionDigits: number = 1,
): string {
  // Intl percent style multiplies by 100, so divide first.
  return getFormatter({
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value / 100);
}

/**
 * Format a signed delta (e.g. "+3.2pp" / "−1.5pp").
 * The `suffix` defaults to "pp" (percentage points) but can be
 * overridden.  Uses the locale's minus sign.
 *
 * Examples: formatDelta(3.2, 1)     → "+3.2pp"  (en) / "+٣٫٢pp" (ar)
 *           formatDelta(-1.5, 1)    → "−1.5pp"  (en) / "−١٫٥pp" (ar)
 *           formatDelta(0.5, 1, "%") → "+0.5%"
 */
export function formatDelta(
  value: number,
  fractionDigits: number = 1,
  suffix: string = 'pp',
): string {
  const formatted = getFormatter({
    signDisplay: 'exceptZero',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
  return `${formatted}${suffix}`;
}

/**
 * Format a compact number for display (e.g. follower counts).
 *
 * Examples: formatCompact(1234)   → "1.2K" (en) / "١٫٢ ألف" (ar)
 *           formatCompact(45600)  → "45.6K"
 */
export function formatCompact(value: number): string {
  return getFormatter({
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Invalidate the formatter cache.
 * Call this when the user changes their language preference
 * so subsequent calls pick up the new locale.
 */
export function clearNumberFormatCache(): void {
  cache.clear();
}
